import { colors } from 'style'
import { util } from 'fabric'

/*
 * Guidelines.
 */

const CENTER_LINE_COLOR = colors.blueDark
const CENTER_LINE_WIDTH = 3
const ALIGN_LINE_COLOR = colors.greenDark
const ALIGN_LINE_WIDTH = 3
const TOLERANCE = 5

function isNear(val1, val2, tol = TOLERANCE) {
  return Math.abs(val1 - val2) < tol
}

function getObjectAnchors(object) {
  const centerPoint = object.getCenterPoint()
  const width = object.getScaledWidth()
  const height = object.getScaledHeight()
  const angle = util.degreesToRadians(object.angle)

  const cosine = Math.cos(angle)
  const sine = Math.sin(angle)

  const halfWidth = width / 2
  const halfHeight = height / 2

  const offset = (x, y) => ({
    x: centerPoint.x + (x * cosine - y * sine),
    y: centerPoint.y + (x * sine + y * cosine),
    originX: x === 0 ? 'center' : x < 0 ? 'left' : 'right',
    originY: y === 0 ? 'center' : y < 0 ? 'top' : 'bottom',
  })

  return {
    left: offset(-halfWidth, 0),
    right: offset(halfWidth, 0),
    centerX: centerPoint,
    top: offset(0, -halfHeight),
    bottom: offset(0, halfHeight),
    centerY: centerPoint,
    topLeft: offset(-halfWidth, -halfHeight),
    topRight: offset(halfWidth, -halfHeight),
    bottomLeft: offset(-halfWidth, halfHeight),
    bottomRight: offset(halfWidth, halfHeight),
  }
}

export function enableAligningGuidelines(fabricCanvas) {
  // TODO: Think about implementing lines for multiple objects.
  if (fabricCanvas.__alignGuidelinesInstalled) return
  fabricCanvas.__alignGuidelinesInstalled = true
  fabricCanvas.__activeAlignGuidelines = []

  fabricCanvas.on('object:moving', function (event) {
    const movingObject = event.target
    if (!movingObject || movingObject.type === 'activeselection') return

    const zoomLevel = fabricCanvas.getZoom()
    const toleranceInCanvas = TOLERANCE / zoomLevel
    let renderedGuidelines = []

    let closestXAxisMatch = { distance: toleranceInCanvas + 1 }
    let closestYAxisMatch = { distance: toleranceInCanvas + 1 }

    // Drawing lines is buggy if one of the objects is rotated.
    // We would need to calculate the shape boundaries for each shape in order for it to work properly.
    if ((movingObject.angle || 0) !== 0) {
      fabricCanvas.__activeAlignGuidelines = []
      return
    }

    const movingAnchors = getObjectAnchors(movingObject)

    fabricCanvas.forEachObject(function (otherObject) {
      if (otherObject === movingObject) return
      // See above for why we skip rotated objects.
      if ((otherObject.angle || 0) !== 0) return

      const stationaryAnchors = getObjectAnchors(otherObject)

      const anchorKeys = [
        'left',
        'centerX',
        'right',
        'top',
        'bottom',
        'centerY',
        'topLeft',
        'topRight',
        'bottomLeft',
        'bottomRight',
      ]

      // For X alignment (vertical lines)
      anchorKeys.forEach((movingAnchorKey) => {
        anchorKeys.forEach((stationaryAnchorKey) => {
          const distance = Math.abs(
            movingAnchors[movingAnchorKey].x -
              stationaryAnchors[stationaryAnchorKey].x,
          )
          if (distance < closestXAxisMatch.distance) {
            closestXAxisMatch = {
              distance,
              snapX: stationaryAnchors[stationaryAnchorKey].x,
              originX: movingAnchors[movingAnchorKey].originX,
              originY: movingAnchors[movingAnchorKey].originY,
              guideline: {
                x: stationaryAnchors[stationaryAnchorKey].x,
                orientation: 'vertical',
              },
            }
          }
        })
      })

      // For Y alignment (horizontal lines)
      anchorKeys.forEach((movingAnchorKey) => {
        anchorKeys.forEach((stationaryAnchorKey) => {
          const distance = Math.abs(
            movingAnchors[movingAnchorKey].y -
              stationaryAnchors[stationaryAnchorKey].y,
          )
          if (distance < closestYAxisMatch.distance) {
            closestYAxisMatch = {
              distance,
              snapY: stationaryAnchors[stationaryAnchorKey].y,
              originX: movingAnchors[movingAnchorKey].originX,
              originY: movingAnchors[movingAnchorKey].originY,
              guideline: {
                y: stationaryAnchors[stationaryAnchorKey].y,
                orientation: 'horizontal',
              },
            }
          }
        })
      })
    })

    let snappedX = null,
      snappedY = null
    let snapOriginX = 'center',
      snapOriginY = 'center'

    if (closestXAxisMatch.distance <= toleranceInCanvas) {
      snappedX = closestXAxisMatch.snapX
      snapOriginX = closestXAxisMatch.originX
      renderedGuidelines.push(closestXAxisMatch.guideline)
    }
    if (closestYAxisMatch.distance <= toleranceInCanvas) {
      snappedY = closestYAxisMatch.snapY
      snapOriginY = closestYAxisMatch.originY
      renderedGuidelines.push(closestYAxisMatch.guideline)
    }

    if (snappedX !== null || snappedY !== null) {
      const currentAnchorPos = movingObject.getPointByOrigin(
        snapOriginX,
        snapOriginY,
      )
      const newAnchorPos = {
        x: snappedX !== null ? snappedX : currentAnchorPos.x,
        y: snappedY !== null ? snappedY : currentAnchorPos.y,
      }
      movingObject.setPositionByOrigin(newAnchorPos, snapOriginX, snapOriginY)
      movingObject.setCoords()
    }

    fabricCanvas.__activeAlignGuidelines = renderedGuidelines
    if (renderedGuidelines.length) fabricCanvas.requestRenderAll()
  })

  fabricCanvas.on('selection:cleared', function () {
    fabricCanvas.__activeAlignGuidelines = []
    fabricCanvas.requestRenderAll()
  })

  fabricCanvas.on('after:render', function () {
    const ctx = fabricCanvas.getContext('2d')
    const vp = fabricCanvas.viewportTransform
    const logicalWidth = fabricCanvas.getWidth()
    const logicalHeight = fabricCanvas.getHeight()

    ctx.save()
    ctx.strokeStyle = ALIGN_LINE_COLOR
    ctx.lineWidth = ALIGN_LINE_WIDTH
    ctx.setLineDash([30, 20])

    fabricCanvas.__activeAlignGuidelines.forEach((guideline) => {
      ctx.beginPath()

      if (guideline.orientation === 'vertical') {
        const top = util.transformPoint({ x: guideline.x, y: 0 }, vp)
        const bottom = util.transformPoint(
          { x: guideline.x, y: logicalHeight },
          vp,
        )
        ctx.moveTo(top.x, top.y)
        ctx.lineTo(bottom.x, bottom.y)
      } else if (guideline.orientation === 'horizontal') {
        const left = util.transformPoint({ x: 0, y: guideline.y }, vp)
        const right = util.transformPoint(
          { x: logicalWidth, y: guideline.y },
          vp,
        )
        ctx.moveTo(left.x, left.y)
        ctx.lineTo(right.x, right.y)
      }

      ctx.stroke()
    })

    ctx.restore()
  })
}

function getAbsoluteCanvasCenter(canvas) {
  return {
    x: canvas.getWidth() / 2,
    y: canvas.getHeight() / 2,
  }
}

function setObjectCenter(obj, centerPoint) {
  obj.set({
    left: centerPoint.x - obj.getScaledWidth() / 2,
    top: centerPoint.y - obj.getScaledHeight() / 2,
  })
  obj.setCoords()
}

export function enableCenteringGuidelines(canvas) {
  if (canvas.__centerGuidelinesInstalled) return
  canvas.__centerGuidelinesInstalled = true

  canvas.__drawVerticalGuide = false
  canvas.__drawHorizontalGuide = false

  canvas.on('object:moving', function (e) {
    const activeObj = e.target
    // TODO: Think about implementing lines for multiple objects.
    if (activeObj.type === 'activeselection') return

    const objCenter = activeObj.getCenterPoint()
    const absCenter = getAbsoluteCanvasCenter(canvas)

    const tol = TOLERANCE / canvas.getZoom()

    let snapX = false,
      snapY = false
    if (isNear(objCenter.x, absCenter.x, tol)) {
      snapX = true
      setObjectCenter(activeObj, { x: absCenter.x, y: objCenter.y })
    }
    if (isNear(objCenter.y, absCenter.y, tol)) {
      snapY = true
      setObjectCenter(activeObj, {
        x: activeObj.getCenterPoint().x,
        y: absCenter.y,
      })
    }

    canvas.__drawVerticalGuide = snapX
    canvas.__drawHorizontalGuide = snapY
    canvas.requestRenderAll()
  })

  canvas.on('selection:cleared', function () {
    canvas.__drawVerticalGuide = false
    canvas.__drawHorizontalGuide = false
    canvas.requestRenderAll()
  })

  canvas.on('after:render', function () {
    const ctx = canvas.getContext('2d')
    const vp = canvas.viewportTransform
    const absCenter = getAbsoluteCanvasCenter(canvas)

    ctx.save()
    ctx.strokeStyle = CENTER_LINE_COLOR
    ctx.lineWidth = CENTER_LINE_WIDTH

    if (canvas.__drawVerticalGuide) {
      const ptTop = util.transformPoint({ x: absCenter.x, y: 0 }, vp)
      const ptBottom = util.transformPoint(
        { x: absCenter.x, y: canvas.getHeight() },
        vp,
      )
      ctx.beginPath()
      ctx.moveTo(ptTop.x, ptTop.y)
      ctx.lineTo(ptBottom.x, ptBottom.y)
      ctx.stroke()
    }
    if (canvas.__drawHorizontalGuide) {
      const ptLeft = util.transformPoint({ x: 0, y: absCenter.y }, vp)
      const ptRight = util.transformPoint(
        { x: canvas.getWidth(), y: absCenter.y },
        vp,
      )
      ctx.beginPath()
      ctx.moveTo(ptLeft.x, ptLeft.y)
      ctx.lineTo(ptRight.x, ptRight.y)
      ctx.stroke()
    }
    ctx.restore()
  })
}
